import React, { useEffect, useMemo, useState } from 'react';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import './TeacherAllSchedules.css';

const WEEKDAYS = ['Ahd', 'Isn', 'Sel', 'Rab', 'Kha', 'Jum', 'Sab'];
const MONTHS = ['Januari', 'Februari', 'Mac', 'April', 'Mei', 'Jun', 'Julai', 'Ogos', 'September', 'Oktober', 'November', 'Disember'];

const toLocalISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TeacherAllSchedules = () => {
  const navigate = useNavigate();
  const todayISO = toLocalISODate(new Date());

  const [userData, setUserData] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date(`${todayISO.slice(0, 7)}-01T00:00:00`));
  const [selectedDate, setSelectedDate] = useState(todayISO);
  const [selectedType, setSelectedType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [toast, setToast] = useState({ show: false, type: 'ok', msg: '' });

  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    setTimeout(() => setToast({ show: false, type: 'ok', msg: '' }), 2600);
  };

  const formatMalayDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate + 'T00:00:00');
    const days = ['Ahad', 'Isnin', 'Selasa', 'Rabu', 'Khamis', 'Jumaat', 'Sabtu'];
    const months = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogos', 'Sep', 'Okt', 'Nov', 'Dis'];
    return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hh, mm] = timeStr.split(':');
    const h = Number(hh);
    if (Number.isNaN(h)) return timeStr;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayH = h % 12 === 0 ? 12 : h % 12;
    return `${displayH}:${mm} ${ampm}`;
  };

  const getTypeLabel = (type) => {
    if (!type) return 'Aktiviti';
    if (type === 'PlayAndTalk') return 'Play & Talk';
    if (type === 'IVC') return 'Kelas IVC';
    if (type === 'Workshop') return 'Bengkel';
    if (type === 'Quiz') return 'Kuiz';
    if (type === 'Seminar') return 'Seminar';
    return type;
  };

  const getTypeClass = (type) => {
    if (type === 'PlayAndTalk') return 'play';
    if (type === 'Workshop') return 'workshop';
    if (type === 'Quiz') return 'quiz';
    if (type === 'Seminar') return 'seminar';
    return 'ivc';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          navigate('/login');
          return;
        }

        const userSnap = await getDoc(doc(db, 'users', user.uid));
        if (!userSnap.exists()) {
          navigate('/login');
          return;
        }

        const data = { uid: user.uid, ...userSnap.data() };

        if (data.role !== 'teacher') {
          navigate(`/dashboard/${data.role}`);
          return;
        }

        setUserData(data);

        const schedulesRef = collection(db, 'schedules');
        const q = query(
          schedulesRef,
          where('daerah', '==', data.daerah),
          where('terminal', '==', data.terminal),
          where('active', '==', true)
        );

        const qs = await getDocs(q);
        const list = [];

        qs.forEach((docSnap) => {
          const item = docSnap.data();

          const subject = item.subject?.toLowerCase() || '';
          const teacher = item.teacher?.toLowerCase() || '';
          const teacherSubject = data.subject?.toLowerCase() || '';
          const teacherName = data.fullName?.toLowerCase() || '';

          const subjectMatch =
            subject.includes(teacherSubject) ||
            teacher.includes(teacherName);

          if (subjectMatch) {
            list.push({
              id: docSnap.id,
              ...item
            });
          }
        });

        list.sort((a, b) => {
          const dateCompare = (a.date || '').localeCompare(b.date || '');
          if (dateCompare !== 0) return dateCompare;
          return (a.startTime || '').localeCompare(b.startTime || '');
        });

        setSchedules(list);
      } catch (error) {
        console.error('Error fetching schedules:', error);
        showToast('bad', 'Ralat load jadual.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate]);

  const filteredSchedules = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    return schedules.filter((schedule) => {
      const matchType = selectedType === 'all' ? true : schedule.type === selectedType;
      const matchSearch = search
        ? (
            schedule.subject?.toLowerCase().includes(search) ||
            schedule.type?.toLowerCase().includes(search) ||
            schedule.teacher?.toLowerCase().includes(search) ||
            schedule.daerah?.toLowerCase().includes(search) ||
            schedule.terminal?.toLowerCase().includes(search) ||
            formatMalayDate(schedule.date).toLowerCase().includes(search)
          )
        : true;

      return matchType && matchSearch;
    });
  }, [schedules, searchTerm, selectedType]);

  const schedulesByDate = useMemo(() => {
    return filteredSchedules.reduce((groups, schedule) => {
      if (!schedule.date) return groups;
      if (!groups[schedule.date]) groups[schedule.date] = [];
      groups[schedule.date].push(schedule);
      return groups;
    }, {});
  }, [filteredSchedules]);

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    return Array.from({ length: 42 }, (_, index) => {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + index);
      const iso = toLocalISODate(date);

      return {
        date,
        iso,
        isCurrentMonth: date.getMonth() === month,
        isToday: iso === todayISO,
        isSelected: iso === selectedDate,
        events: schedulesByDate[iso] || [],
      };
    });
  }, [calendarMonth, schedulesByDate, selectedDate, todayISO]);

  const selectedDateEvents = useMemo(() => schedulesByDate[selectedDate] || [], [schedulesByDate, selectedDate]);
  const monthLabel = `${MONTHS[calendarMonth.getMonth()]} ${calendarMonth.getFullYear()}`;
  const upcomingCount = filteredSchedules.filter((schedule) => schedule.date && schedule.date >= todayISO).length;

  const moveMonth = (direction) => {
    setCalendarMonth((current) => {
      const next = new Date(current);
      next.setMonth(current.getMonth() + direction);
      return next;
    });
  };

  const goToday = () => {
    setCalendarMonth(new Date(`${todayISO.slice(0, 7)}-01T00:00:00`));
    setSelectedDate(todayISO);
  };

  const isPastSession = (dateStr) => {
    if (!dateStr) return false;
    return dateStr < todayISO;
  };

  const handleAttendance = (schedule) => {
    showToast('ok', `Modul kehadiran untuk ${schedule.subject || 'kelas'} akan datang.`);
    // Bila route dah siap nanti:
    // navigate('/dashboard/teacher/attendance', { state: { schedule, teacherData: userData } });
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="tas-wrap">
        <div className="tas-card">
          <div className="tas-spinner"></div>
          <p>Memuatkan semua jadual...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tas-wrap">
      {toast.show && (
        <div className={`ios-toast ${toast.type}`}>
          <span className={`ico toast-ico ${toast.type === 'ok' ? 'toast-ico-ok' : 'toast-ico-bad'}`} aria-hidden="true" />
          <span>{toast.msg}</span>
        </div>
      )}

      <div className="tas-top">
        <button
          className="tas-back"
          type="button"
          onClick={() => navigate('/dashboard/teacher')}
          aria-label="Kembali"
        >
          <span aria-hidden="true" />
        </button>
        <button
          className="tas-back tas-logout"
          type="button"
          onClick={handleLogout}
          aria-label="Keluar"
        >
          <span aria-hidden="true" />
        </button>

        <div className="tas-title">
          <span>Kalendar Cikgu</span>
          <h1>{monthLabel}</h1>
          <p>
            {userData?.fullName || 'Guru'} / {userData?.subject || 'Subjek'}
          </p>
        </div>
      </div>

      <div className="tas-toolbar">
        <input
          type="text"
          className="tas-search"
          placeholder="Cari subjek, jenis kelas, tarikh atau lokasi..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />

        <select
          className="tas-type-filter"
          value={selectedType}
          onChange={(event) => setSelectedType(event.target.value)}
        >
          <option value="all">Semua jenis</option>
          <option value="IVC">Kelas IVC</option>
          <option value="PlayAndTalk">Play & Talk</option>
          <option value="Workshop">Bengkel</option>
          <option value="Quiz">Kuiz</option>
          <option value="Seminar">Seminar</option>
        </select>

        <div className="tas-month-actions">
          <button type="button" onClick={goToday}>Hari ini</button>
          <button type="button" onClick={() => moveMonth(-1)} aria-label="Bulan sebelum">&lt;</button>
          <button type="button" onClick={() => moveMonth(1)} aria-label="Bulan seterusnya">&gt;</button>
        </div>

        <div className="tas-count">
          {filteredSchedules.length} kelas
        </div>
      </div>

      {filteredSchedules.length === 0 ? (
        <div className="tas-empty">
          <div className="tas-empty-icon tas-empty-calendar" aria-hidden="true"><span /></div>
          <h3>Tiada jadual dijumpai</h3>
          <p>
            {searchTerm
              ? 'Cuba kata kunci lain untuk carian awak.'
              : 'Belum ada jadual aktif untuk cikgu buat masa ini.'}
          </p>
        </div>
      ) : (
        <section className="tas-calendar-shell">
          <aside className="tas-calendar-side">
            <div className="tas-mini-card">
              <span>Ringkasan Bulan</span>
              <strong>{monthLabel}</strong>
              <div className="tas-mini-grid">
                <div>
                  <b>{upcomingCount}</b>
                  <small>Akan datang</small>
                </div>
                <div>
                  <b>{Object.keys(schedulesByDate).length}</b>
                  <small>Hari kelas</small>
                </div>
              </div>
            </div>

            <div className="tas-legend">
              <span><i className="dot ivc" /> IVC</span>
              <span><i className="dot play" /> Play & Talk</span>
              <span><i className="dot workshop" /> Bengkel</span>
            </div>
          </aside>

          <main className="tas-calendar-main">
            <div className="tas-weekdays">
              {WEEKDAYS.map((day) => (
                <div key={day}>{day}</div>
              ))}
            </div>

            <div className="tas-calendar-grid">
              {calendarDays.map((day) => (
                <button
                  key={day.iso}
                  type="button"
                  className={`tas-day ${day.isCurrentMonth ? '' : 'muted'} ${day.isToday ? 'today' : ''} ${day.isSelected ? 'selected' : ''}`}
                  onClick={() => setSelectedDate(day.iso)}
                >
                  <span className="tas-day-number">{day.date.getDate()}</span>
                  <div className="tas-day-events">
                    {day.events.slice(0, 3).map((event) => (
                      <span key={event.id} className={`tas-event-chip ${getTypeClass(event.type)}`}>
                        {event.subject || getTypeLabel(event.type)}
                      </span>
                    ))}
                    {day.events.length > 3 && (
                      <span className="tas-event-more">+{day.events.length - 3} lagi</span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </main>

          <aside className="tas-event-panel">
            <div className="tas-event-panel-head">
              <span>Tarikh dipilih</span>
              <h2>{formatMalayDate(selectedDate)}</h2>
              <p>{selectedDateEvents.length} kelas / aktiviti</p>
            </div>

            {selectedDateEvents.length === 0 ? (
              <div className="tas-panel-empty">
                <strong>Tiada kelas</strong>
                <p>Tak ada kelas untuk tarikh ini dalam pilihan semasa.</p>
              </div>
            ) : (
              <div className="tas-panel-list">
                {selectedDateEvents.map((schedule) => (
                  <article key={schedule.id} className={`tas-panel-card ${isPastSession(schedule.date) ? 'past' : ''}`}>
                    <div className="tas-panel-time">
                      <strong>{formatTime(schedule.startTime)}</strong>
                      <span>{schedule.endTime ? formatTime(schedule.endTime) : 'Tamat belum ada'}</span>
                    </div>

                    <div className="tas-panel-body">
                      <div className="tas-panel-title">
                        <h3>{schedule.subject || 'Subjek'}</h3>
                        <span className={`tas-event-type ${getTypeClass(schedule.type)}`}>{getTypeLabel(schedule.type)}</span>
                      </div>
                      <p>{schedule.daerah || '-'} / {schedule.terminal || '-'}</p>
                      {schedule.room ? <p>Bilik {schedule.room}</p> : null}
                      {isPastSession(schedule.date) ? <span className="tas-done">Selesai</span> : null}

                      <div className="tas-actions">
                        <button
                          className="tas-btn tas-secondary"
                          type="button"
                          onClick={() => handleAttendance(schedule)}
                        >
                          Akan Datang
                        </button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </aside>
        </section>
      )}
    </div>
  );
};

export default TeacherAllSchedules;
